/**
 * Das Echo einer Nachricht MIT Bild — eine Karte, nicht zwei. (16.10.2026)
 *
 * Martin, 21:37: „Wenn ich ein Bild schicke mit Textanhang, dann kommen zwei Cards, eine mit Bild und die andere ohne Bild mit dem
 * gleichen Text.“ In der Datenbank steht die Nachricht EINMAL (user, Text, `metadata.imageUrls`).
 *
 * Ursache: die sendende Seite zeichnet sofort eine Bildblase (`bubble image user`, Verlaufsart „image“, ohne `data-text`). Das Echo
 * vom Strom (/api/chat/stream) kommt danach als `user` mit demselben Text — und die Doppel-Prüfung im Strom-Hörer kannte nur Verlaufsart
 * „user“ und `.bubble.user[data-text]`; die Bildblase fiel durch, der Text wurde ein zweites Mal ohne Bild gezeichnet. Der Verlaufs-Abgleich
 * (hydrateFromServer) hat das seit 05.09.2026 über die Bildadresse gelöst; der Strom-Hörer nicht.
 *
 * Die stabile Kennung ist die Adresse der Ablage (`/uploads/<konto>/<uuid>.<art>`), nie der Text: zwei Nachrichten mit gleichem Text
 * und verschiedenen (oder ohne) Bilder bleiben zwei Karten.
 *
 * Notschalter: localStorage `voice-v8:bild-echo` = „0“ oder „aus“ → altes Verhalten (nur die Text-Prüfung). Vorgabe an.
 */

/** Höchstens so viele Adressen je Nachricht werden gelesen (der Server nimmt nicht mehr an). */
const ADRESSEN_HOECHSTENS = 10;
const SCHALTER = "voice-v8:bild-echo";

/** Nur eigene Ablage-Adressen (`/uploads/…`), ohne Anfrageteil; alles andere ist keine Kennung. Rein. */
export function ablageAdresse(roh: unknown): string | null {
  if (typeof roh !== "string" || roh.length > 512) return null;
  let pfad = roh.trim();
  try {
    if (/^https?:\/\//i.test(pfad)) pfad = new URL(pfad).pathname;
  } catch {
    return null;
  }
  pfad = pfad.split(/[?#]/)[0] ?? "";
  return /^\/uploads\/[A-Za-z0-9_./-]+$/.test(pfad) && !pfad.includes("..") ? pfad : null;
}

/** Die Bildadressen eines Strom-Ereignisses (`imageUrls`, Rückfall `imageUrl`) — nur gültige, ohne Doppelte. Rein. */
export function bildAdressenAusEcho(m: { imageUrls?: unknown; imageUrl?: unknown }): string[] {
  const roh: unknown[] = Array.isArray(m.imageUrls) ? m.imageUrls : m.imageUrl !== undefined ? [m.imageUrl] : [];
  const aus = new Set<string>();
  for (const r of roh.slice(0, ADRESSEN_HOECHSTENS)) {
    const a = ablageAdresse(r);
    if (a) aus.add(a);
  }
  return [...aus];
}

/** Ist der Notschalter an (Vorgabe)? Ein Fehler beim Lesen gilt als an. */
export function bildEchoAn(): boolean {
  try {
    const w = localStorage.getItem(SCHALTER)?.trim().toLowerCase();
    return w !== "0" && w !== "aus";
  } catch {
    return true;
  }
}

/**
 * Steht eine dieser Adressen schon im Chat — als Bildblase im DOM oder als Verlaufseintrag der Art „image“? Dann ist die ganze
 * Nachricht schon da. Rein bis auf das Lesen des DOM.
 */
export function bildSchonGezeichnet(
  adressen: readonly string[],
  chat: ParentNode,
  verlauf: ReadonlyArray<{ kind?: string; image?: { url?: string | null } | null }>,
): boolean {
  if (!adressen.length) return false;
  const da = new Set<string>();
  for (const e of verlauf) {
    if (e.kind !== "image") continue;
    const a = ablageAdresse(e.image?.url);
    if (a) da.add(a);
  }
  for (const img of chat.querySelectorAll<HTMLImageElement>(".bubble.image img")) {
    const a = ablageAdresse(img.getAttribute("src"));
    if (a) da.add(a);
  }
  return adressen.some((a) => da.has(a));
}
